import React, { useEffect, useState, useCallback } from "react";
import { StarRating } from "@/components/StarRating";
import { MessageSquare, Send, Loader2, User, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { SITE_CONTAINER } from "@/lib/siteLayout";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("hu-HU", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * Vélemény szekció a főoldalon — az AutoPark szolgáltatásra vonatkozóan.
 * - Csillag értékelés + szöveges vélemény.
 * - Lista formában megjeleníti az összes véleményt.
 * - JSON-LD Organization/Product-szintű AggregateRating + Review a <head>-be
 *   dinamikusan injectelve (SEO + AI keresők).
 */
export function SiteReviews() {
  const { content } = useSiteContent();
  const sr = content.site_reviews || DEFAULT_SITE_CONTENT.site_reviews;
  const orgName = content.brand?.organization_name || "AutoPark";
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [authorName, setAuthorName] = useState("");
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/site-reviews");
      setReviews(r.data.reviews || []);
      setAggregate(r.data.aggregate || { count: 0, average: 0 });
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // JSON-LD SEO — Organization + AggregateRating + Review
  useEffect(() => {
    const url = typeof window !== "undefined" ? window.location.origin : "";
    const data = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": url,
      name: "AutoPark",
      url,
      description: "Minőségi, ellenőrzött használt autók egyenesen a kereskedőktől.",
    };
    if (aggregate && aggregate.count > 0) {
      data.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: aggregate.average,
        reviewCount: aggregate.count,
        bestRating: 5,
        worstRating: 1,
      };
    }
    if (reviews && reviews.length > 0) {
      data.review = reviews.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author_name },
        datePublished: r.created_at,
        reviewBody: r.text,
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
      }));
    }
    const id = "ld-site-reviews";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById(id);
      if (node) node.remove();
    };
  }, [aggregate, reviews]);

  const submit = async (e) => {
    e.preventDefault();
    if (!authorName.trim() || !rating || !text.trim()) {
      toast.error("Kérjük, tölts ki minden mezőt és adj csillagos értékelést.");
      return;
    }
    if (text.trim().length < 5) {
      toast.error("A vélemény legalább 5 karakter legyen.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/site-reviews", {
        author_name: authorName.trim(),
        rating,
        text: text.trim(),
      });
      setAuthorName("");
      setRating(0);
      setText("");
      toast.success("Köszönjük a véleményed!");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "A vélemény mentése sikertelen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="velemenyek"
      data-testid="site-reviews-section"
      className="py-20 scroll-mt-28"
      itemScope
      itemType="https://schema.org/Organization"
    >
      <meta itemProp="name" content={orgName} />
      <div className={SITE_CONTAINER}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-slate-400 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-electric" strokeWidth={1.75} /> {sr.eyebrow}
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-light mt-1">
              {sr.title}
            </h2>
            <p className="text-slate-400 mt-2 text-sm md:text-base max-w-2xl">
              {sr.description}
            </p>
          </div>
          {aggregate.count > 0 && (
            <div
              className="glass-strong rounded-2xl px-5 py-3 flex items-center gap-3"
              data-testid="site-reviews-aggregate"
              itemProp="aggregateRating"
              itemScope
              itemType="https://schema.org/AggregateRating"
            >
              <div className="text-center">
                <div className="font-heading text-4xl font-light text-electric leading-none" itemProp="ratingValue" data-testid="site-reviews-avg">
                  {aggregate.average.toFixed(1)}
                </div>
                <div className="text-[10px] tracking-wide uppercase text-slate-400 mt-1">/ 5</div>
              </div>
              <div>
                <StarRating value={aggregate.average} size="md" />
                <div className="text-xs text-slate-400 mt-1">
                  <span itemProp="reviewCount" data-testid="site-reviews-count">{aggregate.count}</span> értékelés
                </div>
                <meta itemProp="bestRating" content="5" />
                <meta itemProp="worstRating" content="1" />
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 md:p-6 rounded-2xl glass"
          data-testid="site-review-form"
        >
          <div className="md:col-span-2 text-xs text-emerald-300 -mb-2" data-testid="site-review-reward-hint">
            {sr.reward_hint}
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{sr.name_label}</label>
            <input
              data-testid="site-review-name"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              maxLength={60}
              placeholder={sr.name_placeholder}
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm focus:border-electric outline-none"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{sr.rating_label}</label>
            <div className="h-11 flex items-center">
              <StarRating value={rating} onChange={setRating} size="md" testid="site-review-star" />
              <span className="ml-3 text-sm text-slate-400" data-testid="site-review-star-value">
                {rating ? `${rating} / 5` : sr.rating_empty}
              </span>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{sr.text_label}</label>
            <textarea
              data-testid="site-review-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              maxLength={1500}
              placeholder={sr.text_placeholder}
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm focus:border-electric outline-none resize-none"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              data-testid="site-review-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.75} />}
              {submitting ? sr.submit_loading_label : sr.submit_label}
            </button>
          </div>
        </form>

        {/* List */}
        <div className="mt-10" data-testid="site-reviews-list">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-electric animate-spin" /></div>
          ) : reviews.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm" data-testid="site-reviews-empty">
              {sr.empty_list_message}
            </div>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reviews.map((r) => (
                <li
                  key={r.id}
                  data-testid={`site-review-item-${r.id}`}
                  className="p-5 rounded-2xl glass"
                  itemProp="review"
                  itemScope
                  itemType="https://schema.org/Review"
                >
                  <meta itemProp="datePublished" content={r.created_at} />
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-electric/15 flex items-center justify-center text-electric shrink-0">
                        <User className="w-4 h-4" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0">
                        <div
                          className="font-medium truncate"
                          data-testid="site-review-author"
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          <span itemProp="name">{r.author_name}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5" data-testid="site-review-date">
                          {formatDate(r.created_at)}
                        </div>
                      </div>
                    </div>
                    <div
                      itemProp="reviewRating"
                      itemScope
                      itemType="https://schema.org/Rating"
                      className="shrink-0"
                    >
                      <StarRating value={r.rating} size="sm" />
                      <meta itemProp="ratingValue" content={String(r.rating)} />
                      <meta itemProp="bestRating" content="5" />
                      <meta itemProp="worstRating" content="1" />
                    </div>
                  </div>
                  <p
                    className="text-sm text-slate-300 leading-relaxed mt-3 whitespace-pre-wrap"
                    data-testid="site-review-body"
                    itemProp="reviewBody"
                  >
                    {r.text}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
